import {useCart} from "@shopify/hydrogen-react";
import {DeliveryTime, OrderLike, OrderProduct} from "../accountV2/subscription/api";
import {OrderStatus} from "../accountV2/common";
import {NumberOfPeople} from "../v2/commonTypes";
import {WeekDate} from "../v2/weekDate";
import {recipeCatalog} from "../v2/recipe/recipeCatalog";
import {useEffect, useMemo, useRef} from "react";
import {Address} from "../delivery/useDelivery";
import {AttributeInput, CartLine, Maybe, Merchandise} from "@shopify/hydrogen-react/storefront-api-types";
import {useWeekRecipes} from "../v2/recipe/useWeekRecipes";
import {getSellingPlanIdApi} from "../accountV2/subscription/api/sellingPlanApi";
import type {PartialDeep} from "type-fest";
import {trackInitiateCheckout, trackTrialOrderChecked, trekkieAddedProduct} from "../tracking/eventTracker";
import {useDeliveryDates} from "./useDeliveryDates";
import {useNavigate} from "react-router-dom";

enum AttributeKey {
  DELIVERY_DATE = "deliveryDate",
  DELIVERY_TIME = "deliveryTime",
  NUMBER_OF_PEOPLE = "numberOfPeople",
  NUMBER_OF_PEOPLE_EXPLICIT = "numberOfPeopleExplicit",
  ADDRESS = "address",
  TEST_ORDER = "testOrder",
}

type MealKitAttributeInput = AttributeInput & {
  key: AttributeKey;
};

const ma = (key: AttributeKey, value: string): MealKitAttributeInput => {
  return {key, value};
};

const cartToOrderProducts = (
  cartLines: (PartialDeep<CartLine, {recurseIntoArrays: true}> | undefined)[],
): OrderProduct[] => {
  return cartLines
    .filter(
      (
        it: PartialDeep<CartLine> | undefined,
      ): it is PartialDeep<CartLine> & {merchandise: Merchandise} =>
        it !== undefined && it.merchandise !== undefined,
    )
    .map((it) => {
      return {
        variantId: it.merchandise.id,
        productId: it.merchandise.product.id,
        quantity: it.quantity,
        amount: it.merchandise.price.amount,
        title: it.merchandise.product.title,
      };
    });
};

const getCheckOutUrlParams = () => {
  //https://community.shopify.com/c/shopify-design/cart-use-permalinks-to-pre-load-the-cart/td-p/613702#conversion-tracking
  const addressPrefillParams: {[key: string]: string} = {
    "checkout[shipping_address][first_name]": CUSTOMER?.firstName ?? "",
    "checkout[shipping_address][last_name]": CUSTOMER?.lastName ?? "",
    "checkout[shipping_address][address1]": CUSTOMER?.defaultAddress?.address1 ?? "",
    "checkout[shipping_address][address2]": CUSTOMER?.defaultAddress?.address2 ?? "",
    "checkout[shipping_address][city]": CUSTOMER?.defaultAddress?.city ?? "",
    "checkout[shipping_address][zip]": CUSTOMER?.defaultAddress?.zip ?? "",
    "checkout[email]": CUSTOMER?.email,
  };

  return Object.keys(addressPrefillParams)
    .filter((key) => addressPrefillParams[key])
    .map((key) => `${key}=${addressPrefillParams[key]}`)
    .join("&");
};

export const useMealkitCart = () => {
  const cart = useCart();
  const navigate = useNavigate();

  const getAttributeValue = (key: AttributeKey): Maybe<string> | undefined => {
    if (!cart.attributes) return undefined;
    const attribute = cart.attributes.find((it) => it && it.key === key);
    return attribute?.value;
  };

  const {loadValues: loadRecipes} = useWeekRecipes(
    WeekDate(getAttributeValue(AttributeKey.DELIVERY_DATE)),
    cart.lines.map(x => x.merchandise.product.id),
  );

  const prevStatus = useRef(cart.status);
  const lastAction = useRef<string | null>(null);
  const lastAddedProduct = useRef<string | null>(null);

  const discountCodes = cart.discountCodes
    ?.filter(it => it.applicable)
    ?.map(it => it.code) ?? [];
  const getTotalDiscountAmount = () => {
    if (!cart.cost) {
      return 0;
    }

    const totalAmount = Number(cart.cost.totalAmount.amount);
    const subtotalAmount = Number(cart.cost.subtotalAmount.amount);

    return subtotalAmount - totalAmount;
  };

  const addDiscount = async (discount: string) => {
    const currentDiscountCodes = cart.discountCodes.map(it => it.code);
    if (currentDiscountCodes.some(it => it.toUpperCase() === discount.toUpperCase())) {
      return;
    }

    cart.discountCodesUpdate([...currentDiscountCodes, discount.toUpperCase()]);
  };

  const removeDiscount = async (discountCode: string) => {
    const currentDiscountCodes = cart.discountCodes.map(it => it.code);
    cart.discountCodesUpdate(currentDiscountCodes.filter((it) => it !== discountCode));
  };

  const cartProducts = useMemo(
    () => cart.lines && cartToOrderProducts(cart.lines as CartLine[]),
    [cart.lines],
  );

  const addedProduct = useMemo(
    () =>
      cart.lines &&
      cartToOrderProducts(cart.lines as CartLine[]).find(
        (p) => p.productId === lastAddedProduct.current,
      ),
    [cart.lines],
  );

  useEffect(() => {
    if (
      prevStatus.current !== "idle" &&
      cart.status === "idle" &&
      lastAction.current === "addProduct"
    ) {
      if (addedProduct) {
        trekkieAddedProduct(addedProduct, cart, "cart");
      }
      lastAction.current = null;
      lastAddedProduct.current = null;
    }
    prevStatus.current = cart.status;
  }, [cart, addedProduct]);

  function saveAttribute(key: AttributeKey, value: string): void;
  function saveAttribute(
    input: MealKitAttributeInput | MealKitAttributeInput[],
  ): void;
  function saveAttribute(
    keyOrInput: AttributeKey | MealKitAttributeInput | MealKitAttributeInput[],
    value?: string,
  ): void {
    let newAttributesArray: MealKitAttributeInput[];

    // Check if the function was called with 2 parameters (key & value)
    if (typeof keyOrInput === "string" && value !== undefined) {
      newAttributesArray = [{key: keyOrInput, value}];
    } else {
      // If not, cast the input into an array if it's a single object
      newAttributesArray = Array.isArray(keyOrInput)
        ? keyOrInput
        : [keyOrInput as MealKitAttributeInput];
    }

    const existingAttributes = cart.attributes
      ? cart.attributes.filter((it): it is MealKitAttributeInput =>
        it
          ? !newAttributesArray.some((newAttr) => newAttr.key === it.key)
          : false,
      )
      : [];

    const newAttributes = [...existingAttributes, ...newAttributesArray];
    cart.cartAttributesUpdate(newAttributes);
  }

  const reset = async (newDeliveryDate: string) => {
    if (cart.lines && cart.lines.length > 0) {
      cart.linesRemove(cart.lines.map((it) => it.id));
    }
    safeRunSubsequentCartCall(() =>
      saveAttribute(AttributeKey.DELIVERY_DATE, newDeliveryDate),
    );
  };

  const safeRunSubsequentCartCall = (f: () => void) => setTimeout(f, 880);

  const updateProductVariants = (catalog: {
    variantIdBy(productId: string): string;
  }) => {
    cart.linesUpdate(
      cart.lines.map((it) => ({
        id: it.id,
        merchandiseId: catalog.variantIdBy(it.merchandise.product.id),
        quantity: it.quantity,
      })),
    );
  };

  const getAddress = (): Address | undefined => {
    const address = getAttributeValue(AttributeKey.ADDRESS);

    if (!address) {
      return undefined;
    }

    return JSON.parse(address);
  };

  const deliveryDateInfo = useDeliveryDates(getAddress()?.zip);
  const getCurrentOrDefaultDeliveryDate = (): WeekDate | undefined =>
    WeekDate(getAttributeValue(AttributeKey.DELIVERY_DATE) || deliveryDateInfo.data?.[0]);
  const weekRecipesInfo = useWeekRecipes(getCurrentOrDefaultDeliveryDate());

  const checkAllItemsInDeliveryDate = () => {
    if (!cart.lines || (cart.lines && cart.lines.length === 0)) {
      return true;
    }

    const deliveryDate = getCurrentOrDefaultDeliveryDate();
    if (!deliveryDate) {
      return false;
    }

    const weekRecipes = weekRecipesInfo.values;
    if (weekRecipes === undefined) {
      return true;
    }
    if (!weekRecipes) {
      return false;
    }

    const productIdsInCart = cart.lines.map((line) => line?.merchandise?.product?.id);

    const productIdsInDeliveryDate = weekRecipes.map((recipe) => recipe.id);

    return productIdsInCart.every((productId) => productIdsInDeliveryDate.includes(productId));
  };

  const resetWithDefaultDeliveryDate = async () => {
    const defaultDeliveryDate = deliveryDateInfo.data?.[0];
    if (defaultDeliveryDate) {
      await reset(defaultDeliveryDate);
    }
  };

  const isSubscriptionMeal = (productId: string) => !isAddon(productId);

  const isAddon = (productId: string) =>
    !cart.lines
      .find(it => it.merchandise.product.id === productId)?.sellingPlanAllocation?.sellingPlan;

  const addAddon = async (productId: string) => {
    lastAction.current = "addProduct";
    lastAddedProduct.current = productId;

    const catalog = recipeCatalog(
      NumberOfPeople(getAttributeValue(AttributeKey.NUMBER_OF_PEOPLE)) || 2,
      3,
      await loadRecipes(),
    );

    const existingCartLine = cart.lines.find(it => it?.merchandise?.product?.id === productId);

    if (existingCartLine) {
      cart.linesUpdate([
        {
          id: existingCartLine.id,
          quantity: existingCartLine.quantity + 1,
        },
      ]);
    } else {
      const merchandiseId = catalog.variantIdBy(productId);

      if (!merchandiseId) {
        return;
      }

      cart.linesAdd([
        {
          merchandiseId: merchandiseId,
          quantity: 1,
        },
      ]);
    }
  };

  const addSubscriptionProduct = async (productId: string, newSellingPlanId: string | undefined) => {
    lastAction.current = "addProduct";
    lastAddedProduct.current = productId;

    const catalog = recipeCatalog(
      NumberOfPeople(getAttributeValue(AttributeKey.NUMBER_OF_PEOPLE)) || 2,
      getNumberOfSubscribedRecipes(cart.lines) + 1,
      await loadRecipes(),
    );

    const sellingPlanId = newSellingPlanId ?? await getSellingPlanIdApi(getAttributeValue(AttributeKey.DELIVERY_DATE));

    if (cart.lines && cart.lines.filter((it) => it?.merchandise?.product?.id === productId).length > 0) {
      cart.linesUpdate(
        cart.lines
          .filter(it => isSubscriptionMeal(it.merchandise.product.id))
          .map(it =>
            ({
              id: it.id,
              merchandiseId: catalog.variantIdBy(it.merchandise.product?.id),
              quantity:
                it.merchandise.product.id === productId
                  ? it.quantity + 1
                  : it.quantity,
              sellingPlanId: sellingPlanId,
            })),
      );
    } else {
      const merchandiseId = catalog.variantIdBy(productId);

      if (!merchandiseId) {
        return;
      }

      cart.linesUpdate(
        cart.lines
          .filter(it => isSubscriptionMeal(it.merchandise.product.id))
          .map(it => ({
            id: it.id,
            merchandiseId: catalog.variantIdBy(it.merchandise.product.id),
          })),
      );
      safeRunSubsequentCartCall(() => {
        cart.linesAdd([
          {
            merchandiseId: merchandiseId,
            quantity: 1,
            sellingPlanId: sellingPlanId,
          },
        ]);
      });
    }
  };

  const removeAddon = (productId: string) => {
    const lineToUpdate = cart.lines.find(it => it.merchandise.product.id === productId);
    cart.linesUpdate([
      {
        id: lineToUpdate.id,
        quantity: lineToUpdate.quantity - 1,
      },
    ]);
  };

  const removeSubscriptionProduct = async (productId: string) => {
    const catalog = recipeCatalog(
      NumberOfPeople(getAttributeValue(AttributeKey.NUMBER_OF_PEOPLE)) || 2,
      getNumberOfSubscribedRecipes(cart.lines) - 1,
      await loadRecipes(),
    );
    cart.linesUpdate(
      cart.lines
        .filter(it => isSubscriptionMeal(it.merchandise.product.id))
        .map((it) => ({
          id: it.id,
          merchandiseId: catalog.variantIdBy(it.merchandise.product.id),
          quantity: it.merchandise.product.id === productId ? it.quantity - 1 : it.quantity,
        })),
    );
  };

  const getNumberOfSubscribedRecipes = (cartLines: CartLine[]) => cartLines
    .filter(it => isSubscriptionMeal(it.merchandise.product.id))
    .reduce((total, it) => total + it.quantity, 0);

  return {
    get state() {
      return cart;
    },
    get status() {
      return cart.status;
    },
    asOrder(): OrderLike {
      return {
        date: this.deliveryDate,
        status: OrderStatus.OPEN,
        numberOfPeople: this.numberOfPeople,
        products: this.products,
      };
    },
    get isReady() {
      return cart.status === "idle";
    },
    get isUninitialized() {
      return cart.status === "uninitialized";
    },
    get totalAmount() {
      return cart.cost?.totalAmount?.amount;
    },
    get numberOfMeals() {
      return cart.lines.reduce((total, it) => total + it.quantity, 0);
    },
    get numberOfSubscribedRecipes() {
      return getNumberOfSubscribedRecipes(cart.lines);
    },
    get products(): OrderProduct[] {
      return cartProducts || [];
    },
    get address(): Address {
      return getAddress();
    },
    get deliveryTime(): DeliveryTime {
      const deliveryTime = getAttributeValue(AttributeKey.DELIVERY_TIME);

      if (deliveryTime) {
        return JSON.parse(deliveryTime);
      }
      return undefined;
    },
    get deliveryDate() {
      return getAttributeValue(AttributeKey.DELIVERY_DATE);
    },
    get numberOfPeople(): NumberOfPeople {
      return (
        NumberOfPeople(getAttributeValue(AttributeKey.NUMBER_OF_PEOPLE)) || 2
      );
    },
    get numberOfPeopleExplicit(): Maybe<string> | undefined {
      return getAttributeValue(AttributeKey.NUMBER_OF_PEOPLE_EXPLICIT);
    },
    async getSellingPlanId() {
      const deliveryDate = getAttributeValue(AttributeKey.DELIVERY_DATE);
      return deliveryDate ? await getSellingPlanIdApi(deliveryDate) : undefined;
    },
    setIsTestOrder(value: boolean) {
      saveAttribute(AttributeKey.TEST_ORDER, `${value}`);
      trackTrialOrderChecked(value);
    },
    get isTestOrder(): boolean {
      return JSON.parse(getAttributeValue(AttributeKey.TEST_ORDER) ?? "false");
    },
    async saveNumberOfPeople(value: NumberOfPeople) {
      saveAttribute([
        ma(AttributeKey.NUMBER_OF_PEOPLE, value.toString()),
        ma(AttributeKey.NUMBER_OF_PEOPLE_EXPLICIT, value.toString()),
      ]);
      const catalog = recipeCatalog(
        value,
        getNumberOfSubscribedRecipes(cart.lines),
        await loadRecipes(),
      );
      safeRunSubsequentCartCall(() => updateProductVariants(catalog));
    },
    proceedToAddons: async function() {
      navigate('../lisakastid');
    },
    proceedToCheckout: async function() {
      trackInitiateCheckout(
        cartProducts,
        Number(cart?.cost?.totalAmount?.amount).toFixed(2)
      );
      window.open(`${cart.checkoutUrl}?${getCheckOutUrlParams()}`, "_self");
    },
    create(numberOfPeople: NumberOfPeople, deliveryDate: string) {
      if (cart.attributes && cart.attributes.length > 0) {
        return;
      }
      cart.cartCreate({
        attributes: [
          {
            key: AttributeKey.NUMBER_OF_PEOPLE,
            value: numberOfPeople.toString(),
          },
          {
            key: AttributeKey.DELIVERY_DATE,
            value: deliveryDate,
          },
        ],
      });
    },
    hasProduct(productId: string) {
      return (
        cart.lines.filter((it) => it.merchandise.product.id === productId)
          .length > 0
      );
    },
    removeProduct: async function(productId: string) {
      isAddon(productId) ? removeAddon(productId) : await removeSubscriptionProduct(productId);
    },
    addProduct: async function(productId: string, sellingPlanId?: string) {
      sellingPlanId || isSubscriptionMeal(productId) ? await addSubscriptionProduct(productId, sellingPlanId) : await addAddon(productId);
    },
    addDiscount,
    removeDiscount,
    get totalDiscountAmount() {
      return getTotalDiscountAmount();
    },
    discountCodes,
    checkAllItemsInDeliveryDate,
    resetWithDefaultDeliveryDate,
  };
};
